import { isAny } from 'bpmn-js/lib/util/ModelUtil';
import { getExtensionElementsList } from 'camunda-bpmn-js/lib/util/ExtensionElementsUtil';
import { selfAndAllChildren } from 'diagram-js/lib/util/Elements';
import { v7 as uuidv7 } from 'uuid';
import { initModeler, type TModeler } from '../modeler/initModeler';
import type { Element, RootLike } from 'diagram-js/lib/model/Types';
import { spSiteContext } from '../sp-site-context/sp-site-context';
import { getUser } from '../store/userStore';

export interface IResetProcessDefinitionResult {
    modeler: TModeler;
    xml: string;
}

export const cleanProcessDefinitionBeforeExport = (
    xml: string,
    ignoreProcessProps?: TRootElementKeys[]
) => {
    const modeler = initModeler();
    return modeler.importXML(xml).then((_importXmlResult) => {
        resetRootElement(modeler, ignoreProcessProps);
        return modelerToXml(modeler).then((result) => {
            return result.xml;
        });
    });
};

export const resetProcessDefinition = (
    xml: string,
    ignoreProcessProps?: TRootElementKeys[]
): Promise<IResetProcessDefinitionResult> => {
    const modeler = initModeler();
    return modeler.importXML(xml).then((_importXmlResult) => {
        resetRootElement(modeler, ignoreProcessProps);
        resetTasks(getRootElement(modeler));
        setSiteContext(modeler);
        return modelerToXml(modeler);
    });
};

export const resetProcess = (xml: string): Promise<IResetProcessDefinitionResult> => {
    const modeler = initModeler();
    return modeler.importXML(xml).then((_importXmlResult) => {
        const rootElement = getRootElement(modeler);
        if (rootElement.type === 'bpmn:Process') {
            modeler
                .get('modeling')
                .updateProperties(rootElement as any, { id: 'Process_' + uuidv7() });
        }
        setSiteContext(modeler);
        return modelerToXml(modeler);
    });
};

const modelerToXml = (modeler: TModeler): Promise<IResetProcessDefinitionResult> =>
    modeler
        .get('moddle')
        .toXML(getRootElement(modeler).businessObject.$parent)
        .then((toXmlResult) => {
            if (!toXmlResult.xml) {
                throw new Error('XML-Dokument fehlt');
            }

            return {
                modeler,
                xml: toXmlResult.xml,
            };
        });

type TRootElementKeys =
    | 'historyTimeToLive'
    | 'siteId'
    | 'siteName'
    | 'siteUrl'
    | 'listId'
    | 'listName'
    | 'listPath'
    | 'name'
    | 'id'
    | 'isExecutable'
    | 'startPriority'
    | 'versionTag';

const resetRootElement = (modeler: TModeler, ignoreProps?: TRootElementKeys[]): void => {
    const rootElement = getRootElement(modeler);
    if (rootElement.type === 'bpmn:Process') {
        const newProps: Record<TRootElementKeys, any> = {
            historyTimeToLive: 'P7D',
            listId: undefined,
            siteId: undefined,
            siteName: undefined,
            siteUrl: undefined,
            listName: undefined,
            listPath: undefined,
            name: undefined,
            id: 'Process_' + uuidv7(),
            isExecutable: true,
            startPriority: 500,
            versionTag: undefined,
        };

        if (ignoreProps && ignoreProps.length > 0) {
            for (const ignoreProp of ignoreProps) {
                delete newProps[ignoreProp];
            }
        }

        modeler.get('modeling').updateProperties(rootElement as any, newProps);
    }
};

const getRootElement = (modeler: TModeler): RootLike => modeler.get('canvas').getRootElement();

export const resetTasks = (rootElement: RootLike): void => {
    for (const entry of selfAndAllChildren([rootElement as any])) {
        if (isAny(entry, ['bpmn:Task', 'bpmn:ServiceTask', 'bpmn:ScriptTask', 'bpmn:UserTask'])) {
            resetTaskPermissions(entry);
            resetTaskListActions(entry);
            resetTaskMails(entry);
        }
    }
};

const resetTaskPermissions = (task: Element): void => {
    for (const permissions of getExtensionElementsList(task, 'fism:Permissions')) {
        resetTaskPermissionsFields(permissions.editorPermissions);
        resetTaskPermissionsFields(permissions.readerPermissions);
    }
};

const resetTaskPermissionsFields = (permissions?: Element): void => {
    if (permissions && Object.hasOwn(permissions, 'fields')) {
        delete permissions.fields;
    }
};

const resetTaskListActions = (task: Element): void => {
    for (const entries of getExtensionElementsList(task, 'fism:ListActions')) {
        for (const entry of entries.get('values')) {
            delete entry.sourceListId;
            delete entry.sourceListName;
            delete entry.targetListId;
            delete entry.targetListName;
            delete entry.variableName;
        }
    }
};
const setSiteContext = (modeler: TModeler): void => {
    const rootElement = getRootElement(modeler);
    const user = getUser();
    let site;
    if (user) {
        site = spSiteContext.get(user.siteUrl) as ISiteContext;
    }
    if (rootElement.type === 'bpmn:Process') {
        const newProps: Pick<Record<TRootElementKeys, any>, 'siteId' | 'siteName' | 'siteUrl'> = {
            siteId: site?.siteId,
            siteName: site?.name,
            siteUrl: user?.siteUrl,
        };

        modeler.get('modeling').updateProperties(rootElement as any, newProps);
    }
};

const resetTaskMails = (task: Element): void => {
    const mails = getExtensionElementsList(task, 'fism:Mails');
    if (mails.length > 0) {
        const searchValue = /{{(.*?)}}/g;
        const replacer = (_placeholder: string, _value: any) => '<<Platzhalter>>';
        const replace = (text: string) => text.replaceAll(searchValue, replacer);
        const replaceValue = (obj?: any): void => {
            if (obj?.value) {
                obj.value = replace(obj.value);
            }
        };

        for (const entries of mails) {
            for (const entry of entries.get('values')) {
                delete entry.copytofield;
                delete entry.sendtofield;
                replaceValue(entry.subject);
                replaceValue(entry.body);
            }
        }
    }
};
